(() => {
  if (customElements.get('gr-pulley-upsell')) return;

  let addInProgress = false;
  const apiRoot = () => {
    const root = window.Shopify?.routes?.root || '/';
    return root.endsWith('/') ? root : `${root}/`;
  };

  async function readCart() {
    const response = await fetch(`${apiRoot()}cart.js`, {
      headers: { Accept: 'application/json' },
      cache: 'no-store'
    });
    if (!response.ok) throw new Error('Unable to check your cart. Please try again.');
    return response.json();
  }

  // Keep the existing dialog open; replace only its contents.
  function renderDrawer(html) {
    if (!html) return false;
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const next = parsed.querySelector('#cart-drawer .cart-drawer__inner');
    const current = document.querySelector('#cart-drawer .cart-drawer__inner');
    if (!next || !current) return false;

    const drawer = document.querySelector('#cart-drawer');
    const panel = drawer?.querySelector('dialog');
    const focusWasInDrawer = !!panel?.contains(document.activeElement);
    current.replaceWith(next);

    if (focusWasInDrawer) {
      const target = drawer.querySelector('button[name="checkout"], a[href$="/checkout"], .theme-drawer__close-button');
      target?.focus({ preventScroll: true });
    }
    return true;
  }

  async function refreshDrawer(sectionId, initialHtml) {
    if (renderDrawer(initialHtml)) return;
    const url = new URL(apiRoot(), window.location.origin);
    url.searchParams.set('sections', sectionId);
    const response = await fetch(url, { cache: 'no-store' });
    if (!response.ok) throw new Error('Unable to refresh the cart.');
    const sections = await response.json();
    if (!renderDrawer(sections[sectionId])) throw new Error('Unable to refresh the cart.');
  }

  // Request the actual header section when its cart icon can be identified.
  // Refresh only that icon, preserving the rest of the header and its menus.
  const iconSelectors = ['cart-icon', '[ref="cartIcon"]', '[data-testid="cart-icon"]'];
  function headerCartTarget() {
    for (const selector of iconSelectors) {
      const element = document.querySelector(selector);
      const wrapper = element?.closest('[id^="shopify-section-"]');
      if (wrapper && !element.closest('#cart-drawer')) {
        return { selector, wrapperId: wrapper.id, sectionId: wrapper.id.replace('shopify-section-', '') };
      }
    }
    return null;
  }

  function refreshHeaderIcon(target, sections) {
    if (!target || !sections?.[target.sectionId]) return;
    const parsed = new DOMParser().parseFromString(sections[target.sectionId], 'text/html');
    const current = document.getElementById(target.wrapperId)?.querySelector(target.selector);
    const next = parsed.querySelector(target.selector);
    if (current && next) current.replaceWith(next);
  }

  class GRPulleyUpsell extends HTMLElement {
    connectedCallback() {
      this._controller?.abort();
      this._controller = new AbortController();
      const { signal } = this._controller;

      this.select = this.querySelector('[data-gr-upsell-variant]');
      this.button = this.querySelector('[data-gr-upsell-add]');
      this.price = this.querySelector('[data-gr-upsell-price]');
      this.message = this.querySelector('[data-gr-upsell-message]');
      const data = this.querySelector('[data-gr-upsell-data]');
      if (!this.select || !this.button || !this.price || !this.message || !data) return;

      try {
        this.product = JSON.parse(data.textContent);
      } catch {
        this.button.disabled = true;
        this.showMessage('Please open the product page to select your pulley set.');
        return;
      }

      this.initialPrice = this.price.innerHTML;
      this.select.addEventListener('change', () => {
        this.showMessage('');
        this.updateSelection();
      }, { signal });
      this.button.addEventListener('click', () => this.addSelectedVariant(), { signal });
      this.updateSelection();
    }

    disconnectedCallback() {
      this._controller?.abort();
    }

    selectedVariant() {
      return this.product?.variants.find(variant => String(variant.id) === this.select.value);
    }

    updateSelection() {
      const variant = this.selectedVariant();
      if (variant) this.price.textContent = variant.priceText;
      else this.price.innerHTML = this.initialPrice;
      this.select.disabled = addInProgress;
      this.button.disabled = addInProgress || !variant?.available;
      this.button.textContent = addInProgress ? 'ADDING...' :
        (!this.product.available ? 'CURRENTLY SOLD OUT' :
          (variant?.available ? 'ADD PULLEY SET TO CART' : 'SELECT A SCOOTER MODEL'));
    }

    showMessage(text) {
      this.message.textContent = text;
      this.message.hidden = !text;
    }

    async addSelectedVariant() {
      const variant = this.selectedVariant();
      if (addInProgress || !variant?.available) return;

      addInProgress = true;
      this.showMessage('');
      this.updateSelection();
      let added = false;
      let alreadyPresent = false;
      const sectionId = this.dataset.sectionId;
      const productId = String(this.dataset.productId);

      try {
        // Recheck after the offer was rendered to avoid adding a duplicate.
        const cart = await readCart();
        alreadyPresent = cart.items.some(item => String(item.product_id) === productId);
        if (alreadyPresent) {
          await refreshDrawer(sectionId);
          return;
        }

        const headerTarget = headerCartTarget();
        const sectionIds = [sectionId];
        if (headerTarget && !sectionIds.includes(headerTarget.sectionId)) sectionIds.push(headerTarget.sectionId);

        const response = await fetch(`${apiRoot()}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            items: [{ id: variant.id, quantity: 1 }],
            sections: sectionIds,
            sections_url: apiRoot()
          })
        });
        const result = await response.json();
        if (!response.ok) throw new Error(result.description || result.message || 'Unable to add this variant. Please try again.');
        added = true;

        await refreshDrawer(sectionId, result.sections?.[sectionId]);
        refreshHeaderIcon(headerTarget, result.sections);
        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      } catch (error) {
        if (added || alreadyPresent) {
          // The cart changed successfully. Never encourage a second add.
          this.showMessage('The pulley set is in your cart. Refresh this page to see the updated cart.');
        } else {
          this.showMessage(error.message || 'Unable to add this variant. Please try again.');
        }
      } finally {
        addInProgress = false;
        document.querySelectorAll('gr-pulley-upsell').forEach(offer => offer.updateSelection());
        if ((added || alreadyPresent) && this.isConnected) {
          this.select.disabled = true;
          this.button.disabled = true;
          this.button.textContent = 'PULLEY SET IS IN YOUR CART';
        }
      }
    }
  }

  customElements.define('gr-pulley-upsell', GRPulleyUpsell);
})();
